import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "../../../../components";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { InputErros } from "../../../../helpers";
import {
  ConfiguracaoPesquisaSatisfacaoService,
  notification,
} from "../../../../services";
import shortid from "shortid";

const CampoPersonalizadoItem = ({
  campo,
  onUpdate,
  onDelete,
  onEdit,
  provided,
}) => {
  const getTipoIcon = (type) => {
    const tipo = tiposCampo.find((t) => t.type === type);
    return tipo ? tipo.icon : type;
  };

  const getTipoDescricao = (type) => {
    const tipo = tiposCampo.find((t) => t.type === type);
    return tipo ? tipo.descricao : type;
  };

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={{ width: "100%", ...provided.draggableProps.style }}
    >
      <Grid
        container
        item
        xs={12}
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          mb: 1.5,
          ml: 1,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fff",
        }}
      >
        <Grid
          item
          xs={1}
          sm={0.5}
          {...provided.dragHandleProps}
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
        >
          <i
            className="ph-bold ph-dots-six-vertical"
            style={{ fontSize: 20, color: "#555" }}
          ></i>
        </Grid>

        <Grid item xs={11} sm={7}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem" }}
          >
            Pergunta:
          </Typography>
          <Typography
            variant="body1"
            fontWeight={500}
            sx={{ fontSize: "0.875rem" }}
          >
            {campo.pergunta}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={1.5}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <i
              className={`ph-fill ${getTipoIcon(campo.type)}`}
              style={{ fontSize: 20, color: "#757575" }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              {getTipoDescricao(campo.type)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={0.8}>
          <FormControlLabel
            control={
              <Switch
                checked={campo.obrigatoriedade || false}
                onChange={(e) =>
                  onUpdate({ ...campo, obrigatoriedade: e.target.checked })
                }
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: "0.8rem", ml: 0.5 }}>
                {campo.obrigatoriedade ? "Obrigatório" : "Opcional"}
              </Typography>
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={2}
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          <Tooltip title="Editar campo">
            <Button
              variant="contained"
              size="small"
              onClick={() => onEdit(campo)}
              sx={{
                height: "35px",
                borderRadius: "50%",
                minWidth: "15px",
                boxShadow: "none",
                backgroundColor: "#EDEEF0",
                "&:hover": { backgroundColor: "#B6BABC" },
              }}
            >
              <i className="ph-fill ph-pencil" style={{ fontSize: 15 }}></i>
            </Button>
          </Tooltip>
          <Tooltip title="Remover campo">
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => onDelete(campo.id)}
              sx={{
                height: "35px",
                borderRadius: "50%",
                minWidth: "15px",
                boxShadow: "none",
              }}
            >
              <i
                className="ph-bold ph-trash"
                style={{ color: "#fff", fontSize: 15 }}
              ></i>
            </Button>
          </Tooltip>
        </Grid>
      </Grid>
    </div>
  );
};

const tiposCampo = [
  { id: 2, type: "number", descricao: "Número", icon: "ph-number-square-one" },
  { id: 3, type: "string", descricao: "Texto", icon: "ph-text-t" },
];

export function PesquisaSatisfacaoDialog({ open, setOpen }) {
  const [camposPersonalizados, setCamposPersonalizados] = useState([]);
  const [novoCampo, setNovoCampo] = useState({
    tipo: null,
    pergunta: "",
  });
  const [campoEditandoId, setCampoEditandoId] = useState(null);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const configuracaoPesquisaSatisfacaoService =
    new ConfiguracaoPesquisaSatisfacaoService();

  useEffect(() => {
    if (open) {
      buscarConfiguracaoPesquisaSatisfacao();
    }
  }, [open]);

  const buscarConfiguracaoPesquisaSatisfacao = async () => {
    const response = await configuracaoPesquisaSatisfacaoService.getAll();
    if (!response.isAxiosError) {
      const data = Array.isArray(response.data.rows) ? response.data.rows : [];
      setCamposPersonalizados(data);
    }
  };

  function handleClose() {
    setOpen(false);
  }

  const handleSubmit = async () => {
    const body = camposPersonalizados.map((campo, index) => ({
      ...campo,
      ordem: index + 1,
    }));
    const result = await configuracaoPesquisaSatisfacaoService.cadastrar(body);
    if (!result.isAxiosError) {
      notification.cadastroSucesso();
      handleClose();
    }
  };

  const handleRemoverCampoPersonalizado = (idCampo) => {
    setCamposPersonalizados((prev) =>
      Array.isArray(prev) ? prev.filter((c) => c.id !== idCampo) : [],
    );
    if (campoEditandoId === idCampo) {
      setCampoEditandoId(null);
      setNovoCampo({ tipo: null, pergunta: "" });
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.isArray(camposPersonalizados)
      ? Array.from(camposPersonalizados)
      : [];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setCamposPersonalizados(items);
  };

  const handleAtualizarCampoPersonalizado = (campoAtualizado) => {
    setCamposPersonalizados((prev) =>
      Array.isArray(prev)
        ? prev.map((campo) =>
            campo.id === campoAtualizado.id ? campoAtualizado : campo,
          )
        : [campoAtualizado],
    );
  };

  const handleAdicionarCampoPersonalizado = () => {
    if (!novoCampo.pergunta?.trim()) {
      notification.erroValidacao("Pergunta");
      setInputErro("pergunta");
      return;
    }
    if (!novoCampo.tipo) {
      notification.erroValidacao("Tipo do Campo");
      setInputErro("tipoCampo");
      return;
    }
    if (campoEditandoId) {
      const campoAtualizado = {
        id: campoEditandoId,
        pergunta: novoCampo.pergunta.trim(),
        type: novoCampo.tipo.type,
        obrigatoriedade:
          camposPersonalizados.find((c) => c.id === campoEditandoId)
            ?.obrigatoriedade || false,
      };
      setCamposPersonalizados((prev) =>
        prev.map((campo) =>
          campo.id === campoEditandoId ? campoAtualizado : campo,
        ),
      );
      setCampoEditandoId(null);
    } else {
      const novoCampoCompleto = {
        id: shortid.generate(),
        pergunta: novoCampo.pergunta.trim(),
        type: novoCampo.tipo.type,
        obrigatoriedade: false,
      };
      setCamposPersonalizados((prev) =>
        Array.isArray(prev)
          ? [...prev, novoCampoCompleto]
          : [novoCampoCompleto],
      );
    }

    inputErros.limpar();
    setNovoCampo({ tipo: null, pergunta: "" });
  };

  const iniciarEdicaoCampo = (campo) => {
    const tipoCorrespondente = tiposCampo.find((t) => t.type === campo.type);
    setNovoCampo({
      tipo: tipoCorrespondente || null,
      pergunta: campo.pergunta || "",
    });
    setCampoEditandoId(campo.id);
  };

  const emEdicaoCampo = !!campoEditandoId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Configurações da Pesquisa de Satisfação</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} className="mt-1">
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              {emEdicaoCampo
                ? "Editar Pergunta Personalizada"
                : "Adicionar Pergunta Personalizada"}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={5}>
                <TextField
                  id="pergunta"
                  label="Pergunta"
                  fullWidth
                  value={novoCampo.pergunta}
                  error={inputErros.get("pergunta")}
                  onChange={(e) =>
                    setNovoCampo({ ...novoCampo, pergunta: e.target.value })
                  }
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      document.getElementById("buttonAdicionar")?.click();
                      document.getElementById("pergunta")?.blur();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={5}>
                <Autocomplete
                  options={tiposCampo}
                  getOptionLabel={(option) => option.descricao}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="tipoCampo"
                      label="Tipo do Campo"
                      error={inputErros.get("tipoCampo")}
                    />
                  )}
                  value={novoCampo.tipo}
                  onChange={(e, value) =>
                    setNovoCampo({ ...novoCampo, tipo: value })
                  }
                />
              </Grid>
              <Grid item xs={2}>
                {emEdicaoCampo ? (
                  <Button
                    id="buttonAdicionar"
                    variant="contained"
                    size="small"
                    sx={{
                      height: "35px",
                      borderRadius: "50%",
                      minWidth: "15px",
                      boxShadow: "none",
                    }}
                    onClick={handleAdicionarCampoPersonalizado}
                  >
                    <i
                      className="ph-bold ph-arrows-clockwise"
                      style={{ color: "#fff", fontSize: 15 }}
                    ></i>
                  </Button>
                ) : (
                  <Button
                    id="buttonAdicionar"
                    variant="contained"
                    color="success"
                    size="small"
                    sx={{
                      height: "35px",
                      borderRadius: "50%",
                      minWidth: "15px",
                      boxShadow: "none",
                    }}
                    onClick={handleAdicionarCampoPersonalizado}
                  >
                    <i
                      className="ph-bold ph-plus"
                      style={{ color: "#fff", fontSize: 15 }}
                    ></i>
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>

          {camposPersonalizados.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                Campos Personalizados Configurados
              </Typography>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="campos-personalizados">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{ width: "100%" }}
                    >
                      {camposPersonalizados.map((campo, index) => (
                        <Draggable
                          key={campo.id}
                          draggableId={String(campo.id)}
                          index={index}
                        >
                          {(provided) => (
                            <CampoPersonalizadoItem
                              campo={campo}
                              onUpdate={handleAtualizarCampoPersonalizado}
                              onDelete={handleRemoverCampoPersonalizado}
                              onEdit={iniciarEdicaoCampo}
                              provided={provided}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className="m-3">
        <Button variant="contained" color="padrao" onClick={handleClose}>
          Fechar
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
